// Copyright (c) StrangeBee 2024

import client from '@Client';
import useListQuery from '@Hooks/useListQuery';
import React, { ReactElement } from 'react';
import { PageOutput, PermissionsEnum } from 'thehive-sdk';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { useMemo } from 'react';
import { computeEventList } from '@Organisms/KnowledgeBase/eventList';
import { useLocalMessage } from '@Helpers/locale';
import { KnowledgeBaseCreate } from '@Organisms/KnowledgeBase/KnowledgeBaseCreate/KnowledgeBaseCreate';
import { MarkdownVisualizerModal } from '@Molecules/MarkdownVisualizer/MarkdownVisualizerModal';
import useUserPermissions from '@Hooks/useUserPermissions';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';

import './KnowledgeBaseView.scss';

export const KnowledgeBaseView = (): ReactElement => {
    const i = useLocalMessage();

    const {
        data: pageList = [],
        refetch: listRefetch,
        isFetching,
    } = useListQuery<PageOutput[]>({
        controller: client.PageController,
        queryParams: {
            selector: {
                head: [{ name: 'listOrganisationPage' }],
                name: 'get-all-org-pages',
            },
            sort: ['+_createdAt'],
        },
        fetchOnStart: false,
        manual: true,
    });

    const hasPermissions = useUserPermissions({ permissionsNeeded: [PermissionsEnum.MANAGE_KNOWLEDGE_BASE] });

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.PageController,
                locale: i,
                onSuccess: (_, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                    listRefetch();
                },
                onError: handleNotificationError,
            }),
        [listRefetch, i],
    );

    return (
        <MarkdownVisualizerModal
            onEdit={async (page, pageId) => await eventList.onEdit(page, pageId)}
            hasPermissions={hasPermissions}
            CreateButton={({
                allCategories,
                className,
            }: {
                allCategories: string[];
                className?: string;
            }): ReactElement => (
                <KnowledgeBaseCreate
                    onCreate={eventList.onCreate}
                    allCategories={allCategories}
                    triggerElem={<AddButton className={className} block label={i('page.create.drawer.label')} />}
                />
            )}
            onCreate={eventList.onCreate}
            onDelete={(pageId) => eventList.onDelete(pageId)}
            list={pageList}
            onVisible={() => listRefetch()}
            isEditLoading={isFetching}
        />
    );
};
